import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  PlusIcon,
  BriefcaseIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  DocumentArrowUpIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  UsersIcon,
  DocumentChartBarIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import Dropdown from "../../shared/Dropdown";
import { useAuth } from "../../hooks/useAuth";
import { useConfig } from "../../hooks/useConfig";
import { roles } from "../../constants/roles";

// Lazy-loaded modals
import {
  JobCreateModal,
  InvoiceCreateModal,
  ManifestCreateModal,
  PayoutCreateModal,
  ClientCreateModal,
  DriverCreateModal,
  UserCreateModal,
} from "../../modals";

const OPEN_CREATE_MODAL_EVENT = "attune:tutorial-open-create";

/**
 * Map user authority to the roles.js column name
 */
const authorityToRoleColumn = {
  owner: "Account Holder",
  admin: "Admin",
  user: "User",
  driver: "Driver",
  client: "Client",
  superAdmin: "Account Holder", // superAdmin has all permissions
};

/**
 * Check if user has permission for a specific role ID
 * @param {string} roleId - The role ID to check (e.g., "job.create")
 * @param {string} userAuthority - The user's authority level
 * @returns {boolean}
 */
const hasPermission = (roleId, userAuthority) => {
  // SuperAdmin has all permissions
  if (userAuthority === "superAdmin") return true;

  const roleConfig = roles.find((r) => r["Role id"] === roleId);
  if (!roleConfig) return false;

  const columnName = authorityToRoleColumn[userAuthority];
  if (!columnName) return false;

  // Check if the role column has a truthy value (1 or true)
  return Boolean(roleConfig[columnName]);
};

/**
 * Create options configuration
 * Each option defines the entity, its permission, and appType visibility
 */
const CREATE_OPTIONS = [
  {
    id: "job",
    label: "Job",
    icon: <BriefcaseIcon className="w-4 h-4" />,
    permissionId: "job.create",
    appTypes: ["trucking", "service", "serviceRepair"], // Available in both
  },
  {
    id: "invoice",
    label: "Invoice",
    icon: <BanknotesIcon className="w-4 h-4" />,
    permissionId: "invoice.create",
    appTypes: ["trucking", "service", "serviceRepair"],
  },
  {
    id: "manifest",
    label: "Manifest",
    icon: <ClipboardDocumentListIcon className="w-4 h-4" />,
    permissionId: "manifest.create",
    appTypes: ["trucking"], // Trucking only
  },
  {
    id: "payout",
    label: "Payout",
    icon: <DocumentArrowUpIcon className="w-4 h-4" />,
    permissionId: "payout.create",
    appTypes: ["trucking", "service", "serviceRepair"],
  },
  {
    id: "client",
    label: "Client",
    icon: <BuildingStorefrontIcon className="w-4 h-4" />,
    permissionId: "client.create",
    appTypes: ["trucking", "service", "serviceRepair"],
  },
  {
    id: "driver",
    label: "Driver",
    icon: <TruckIcon className="w-4 h-4" />,
    permissionId: "driver.create",
    appTypes: ["trucking"], // Trucking only
  },
  {
    id: "user",
    label: "User",
    icon: <UsersIcon className="w-4 h-4" />,
    permissionId: null, // No specific permission, uses authority check
    appTypes: ["trucking", "service", "serviceRepair"],
    requiredAuthority: ["owner", "admin", "superAdmin"], // Only admins can create users
  },
  {
    id: "report",
    label: "Report",
    icon: <DocumentChartBarIcon className="w-4 h-4" />,
    permissionId: null, // Uses authority check
    appTypes: ["trucking", "service", "serviceRepair"],
    requiredAuthority: ["owner", "admin", "superAdmin"],
    navigateTo: "/reports?tab=generate", // Navigate instead of modal
  },
];

/**
 * UniversalCreateButton Component
 * Renders a dropdown button that allows quick creation of various entities
 * Options are filtered based on user permissions and appType
 */
const UniversalCreateButton = () => {
  const { user } = useAuth();
  const { appType } = useConfig();
  const navigate = useNavigate();

  // Modal visibility state for each entity type
  const [modals, setModals] = useState({
    job: false,
    invoice: false,
    manifest: false,
    payout: false,
    client: false,
    driver: false,
    user: false,
  });

  /**
   * Open a specific modal
   */
  const openModal = useCallback((modalId) => {
    setModals((prev) => ({ ...prev, [modalId]: true }));
  }, []);

  /**
   * Close a specific modal
   */
  const closeModal = useCallback((modalId) => {
    setModals((prev) => ({ ...prev, [modalId]: false }));
  }, []);

  useEffect(() => {
    const handleExternalOpen = (event) => {
      const modalId = event?.detail?.entity;
      if (!modalId) return;
      openModal(modalId);
    };

    window.addEventListener(OPEN_CREATE_MODAL_EVENT, handleExternalOpen);
    return () => {
      window.removeEventListener(OPEN_CREATE_MODAL_EVENT, handleExternalOpen);
    };
  }, [openModal]);

  /**
   * Filter options based on user permissions and appType
   */
  const filteredOptions = useMemo(() => {
    if (!user?.authority) return [];

    return CREATE_OPTIONS.filter((option) => {
      // Check appType filter
      if (appType && !option.appTypes.includes(appType)) {
        return false;
      }

      // Check required authority (for options without permission ID)
      if (option.requiredAuthority) {
        return option.requiredAuthority.includes(user.authority);
      }

      // Check permission
      if (option.permissionId) {
        return hasPermission(option.permissionId, user.authority);
      }

      return true;
    });
  }, [user?.authority, appType]);

  /**
   * Transform filtered options into dropdown format
   */
  const dropdownOptions = useMemo(() => {
    return filteredOptions.map((option) => ({
      title: option.label,
      icon: option.icon,
      onClick: () => {
        if (option.navigateTo) {
          navigate(option.navigateTo);
        } else {
          openModal(option.id);
        }
      },
    }));
  }, [filteredOptions, openModal, navigate]);

  // Don't render if no options available
  if (dropdownOptions.length === 0) {
    return null;
  }

  const triggerButton = (
    <div className="flex flex-row items-center justify-center h-full w-full cursor-pointer px-1">
      <PlusIcon className="stroke-2 h-5 w-5 text-gray-700" />
    </div>
  );

  return (
    <>
      <Dropdown
        closeOnClick
        data={dropdownOptions}
        trigger={triggerButton}
        className="flex"
        widthClasses="w-48"
      />

      {/* Job Create Modal */}
      <JobCreateModal
        modalOpen={modals.job}
        setModalOpen={(visible) =>
          visible ? openModal("job") : closeModal("job")
        }
      />

      {/* Invoice Create Modal */}
      <InvoiceCreateModal
        modalOpen={modals.invoice}
        setModalOpen={(visible) =>
          visible ? openModal("invoice") : closeModal("invoice")
        }
      />

      {/* Manifest Create Modal */}
      <ManifestCreateModal
        modalOpen={modals.manifest}
        setModalOpen={(visible) =>
          visible ? openModal("manifest") : closeModal("manifest")
        }
      />

      {/* Payout Create Modal */}
      <PayoutCreateModal
        modalOpen={modals.payout}
        setModalOpen={(visible) =>
          visible ? openModal("payout") : closeModal("payout")
        }
      />

      {/* Client Create Modal */}
      <ClientCreateModal
        visible={modals.client}
        setVisible={(visible) =>
          visible ? openModal("client") : closeModal("client")
        }
      />

      {/* Driver Create Modal */}
      <DriverCreateModal
        visible={modals.driver}
        setVisible={(visible) =>
          visible ? openModal("driver") : closeModal("driver")
        }
      />

      {/* User Create Modal */}
      <UserCreateModal
        visible={modals.user}
        setVisible={(visible) =>
          visible ? openModal("user") : closeModal("user")
        }
      />
    </>
  );
};

export default UniversalCreateButton;
export { UniversalCreateButton };

